import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBagIcon, DocumentTextIcon, ClipboardDocumentListIcon,
  ArchiveBoxIcon, TruckIcon, BanknotesIcon, UserGroupIcon, ChartBarIcon,
  PlusIcon, ArrowPathIcon, ArrowRightIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import EmptyState from '../components/EmptyState';
import { fetchB2bOrders, fetchRfqs, fetchBuyerOverview, fetchBuyerDeliveries, fetchBuyerInvoices } from '../api/marketplaceApi';

const BuyerOverview: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>({});
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [openRfqs, setOpenRfqs] = useState<any[]>([]);
  const [activeDeliveries, setActiveDeliveries] = useState<any[]>([]);
  const [pendingInvoices, setPendingInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [overviewRes, ordersRes, rfqsRes, deliveriesRes, invoicesRes] = await Promise.allSettled([
        fetchBuyerOverview().catch(() => null),
        fetchB2bOrders({ limit: 5 }).catch(() => ({ orders: [] })),
        fetchRfqs({ status: 'published', limit: 5 }).catch(() => ({ items: [] })),
        fetchBuyerDeliveries({ status: 'in_transit', limit: 5 }).catch(() => ({ items: [] })),
        fetchBuyerInvoices({ status: 'pending', limit: 5 }).catch(() => ({ items: [] })),
      ]);

      if (overviewRes.status === 'fulfilled' && overviewRes.value) {
        setStats(overviewRes.value.stats || overviewRes.value);
      }
      if (ordersRes.status === 'fulfilled') {
        const orderData = ordersRes.value;
        setRecentOrders(orderData.orders || orderData.items || []);
      }
      if (rfqsRes.status === 'fulfilled') {
        setOpenRfqs(rfqsRes.value.items || []);
      }
      if (deliveriesRes.status === 'fulfilled') {
        setActiveDeliveries(deliveriesRes.value.items || deliveriesRes.value.shipments || []);
      }
      if (invoicesRes.status === 'fulfilled') {
        setPendingInvoices(invoicesRes.value.items || []);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const metrics = [
    {
      label: 'Open Orders',
      value: stats.orders?.confirmed ?? recentOrders.filter(o => ['awaiting_confirmation', 'confirmed'].includes(o.status)).length ?? '-',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      color: 'text-blue-600 bg-blue-100',
      onClick: () => navigate('/marketplace/buyer/orders'),
    },
    {
      label: 'Awaiting Confirmation',
      value: stats.orders?.awaiting ?? recentOrders.filter(o => o.status === 'awaiting_confirmation').length ?? '-',
      icon: <ArchiveBoxIcon className="w-4 h-4" />,
      color: 'text-amber-600 bg-amber-100',
      onClick: () => navigate('/marketplace/buyer/orders'),
    },
    {
      label: 'Active Deliveries',
      value: stats.shipments?.in_transit ?? activeDeliveries.length ?? '-',
      icon: <TruckIcon className="w-4 h-4" />,
      color: 'text-indigo-600 bg-indigo-100',
      onClick: () => navigate('/marketplace/buyer/deliveries'),
    },
    {
      label: 'Outstanding Invoices',
      value: stats.invoices?.pending ?? pendingInvoices.length ?? '-',
      icon: <BanknotesIcon className="w-4 h-4" />,
      color: 'text-rose-600 bg-rose-100',
      onClick: () => navigate('/marketplace/buyer/invoices'),
    },
    {
      label: 'Open RFQs',
      value: stats.rfqs?.open ?? openRfqs.length ?? '-',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      color: 'text-purple-600 bg-purple-100',
      onClick: () => navigate('/marketplace/buyer/rfqs'),
    },
    {
      label: 'Pending Quotations',
      value: stats.quotations?.pending ?? '-',
      icon: <ClipboardDocumentListIcon className="w-4 h-4" />,
      color: 'text-emerald-600 bg-emerald-100',
      onClick: () => navigate('/marketplace/buyer/quotations'),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Procurement Overview</h2>
          <p className="text-sm text-gray-500 mt-0.5">Your organisation's procurement activity at a glance</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => navigate('/marketplace/buyer/catalogue')}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
          >
            <ShoppingBagIcon className="w-4 h-4" /> Browse Marketplace
          </button>
          <button
            onClick={() => navigate('/marketplace/buyer/rfqs/new')}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
          >
            <PlusIcon className="w-4 h-4" /> Create RFQ
          </button>
        </div>
      </div>

      {/* KPI Metric Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {metrics.map((m) => (
          <button
            key={m.label}
            onClick={m.onClick}
            className="bg-white border border-gray-200 rounded-xl p-4 text-left hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${m.color}`}>
                {m.icon}
              </div>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">{m.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{m.label}</p>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: 'Browse Marketplace', path: '/marketplace/buyer/catalogue', icon: <ShoppingBagIcon className="w-4 h-4" /> },
          { label: 'Create RFQ', path: '/marketplace/buyer/rfqs/new', icon: <PlusIcon className="w-4 h-4" /> },
          { label: 'View Orders', path: '/marketplace/buyer/orders', icon: <DocumentTextIcon className="w-4 h-4" /> },
          { label: 'Review Quotations', path: '/marketplace/buyer/quotations', icon: <ClipboardDocumentListIcon className="w-4 h-4" /> },
        ].map(action => (
          <button
            key={action.path}
            onClick={() => navigate(action.path)}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 hover:border-emerald-300 transition-colors"
          >
            {action.icon}
            {action.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Recent Orders</h3>
            <button
              onClick={() => navigate('/marketplace/buyer/orders')}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              View all <ArrowRightIcon className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {recentOrders.length === 0 ? (
              <EmptyState
                title="No orders yet"
                description="Browse the marketplace or create an RFQ to place your first order."
                action={{ label: 'Browse Marketplace', onClick: () => navigate('/marketplace/buyer/catalogue') }}
              />
            ) : (
              recentOrders.map((order: any) => (
                <button
                  key={order.id}
                  onClick={() => navigate(`/marketplace/buyer/orders/${order.id}`)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                >
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                    <DocumentTextIcon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{order.order_number}</p>
                    <p className="text-xs text-gray-400">
                      {order.item_count || order.items?.length || 0} items · {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <StatusBadge status={order.status} size="sm" />
                    <p className="text-xs font-mono text-gray-500 mt-0.5">
                      <Money amount={order.total_amount} currency={order.currency} />
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Open RFQs */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Open RFQs</h3>
            <button
              onClick={() => navigate('/marketplace/buyer/rfqs')}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              View all <ArrowRightIcon className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {openRfqs.length === 0 ? (
              <EmptyState
                title="No open RFQs"
                description="Create a Request for Quotation to start procuring from verified suppliers."
                action={{ label: 'Create RFQ', onClick: () => navigate('/marketplace/buyer/rfqs/new') }}
              />
            ) : (
              openRfqs.map((rfq: any) => (
                <button
                  key={rfq.id}
                  onClick={() => navigate(`/marketplace/buyer/rfqs/${rfq.id}`)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{rfq.title}</p>
                    <p className="text-xs text-gray-400">{rfq.rfq_number}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <StatusBadge status={rfq.status} size="sm" />
                    {rfq.quote_deadline && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Due {new Date(rfq.quote_deadline).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Active Deliveries */}
      {activeDeliveries.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Active Deliveries</h3>
            <button
              onClick={() => navigate('/marketplace/buyer/deliveries')}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              View all <ArrowRightIcon className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {activeDeliveries.slice(0, 3).map((delivery: any) => (
              <div key={delivery.id} className="px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                  <TruckIcon className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {delivery.shipment_number || `SHP-${delivery.id}`}
                  </p>
                  <p className="text-xs text-gray-400">{delivery.carrier_name || 'Carrier TBD'}</p>
                </div>
                <StatusBadge status={delivery.status} size="sm" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Supplier Directory Shortcut */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
            <UserGroupIcon className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Verified Supplier Directory</p>
            <p className="text-xs text-gray-500">Browse verified suppliers and their product catalogues</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/marketplace/buyer/suppliers')}
          className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
        >
          View Suppliers <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default BuyerOverview;
